import React, { useState, useEffect, createContext, useContext, useCallback, } from "react";
import Cookies from "js-cookie"
import { BACKENDURL } from "../../../core/api/constants";
import { ImageService } from "../../../core/service/imageService";
import { UserProfile } from "../../profile/model/userProfileDto";
import ProfileApi from "../../profile/api/profileApi";
import AuthApi from "../api/authApi";
import { UserRole } from "../model/userRole";

type AuthContextType = {
    signIn: (email: string, password: string) => Promise<{ isOk: boolean; message?: string }>;
    signOut: () => void;
    hasRole: (role: UserRole) => boolean;
    hasAnyRole: (...allowedRoles: UserRole[]) => Promise<boolean>;
    isLoggedIn: boolean;
    checkIsLoggedIn: () => boolean;
    loading: boolean;
    user?: UserProfile;
    userProfilePicture?: string;
    roles: UserRole;
};

function AuthProvider(props: React.PropsWithChildren<unknown>) {
    const [loading, setLoading] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState<UserProfile | undefined>(undefined)
    const [roles, setRoles] = useState<UserRole>(UserRole.None)
    const [userProfilePicture, setUserProfilePicture] = useState<string | undefined>(undefined)

    function checkIsLoggedIn() {
        return !!Cookies.get("UserLoginCookie")
    }

    const signOut = useCallback(async () => {
        await fetch(`${BACKENDURL}/api/logout`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
        }).then((response) => {
            setIsLoggedIn(false)
        }).catch((error) => {
            console.error("Logout error:", error);
        }).finally(() => {
            Cookies.remove("UserLoginCookie")
            setIsLoggedIn(false)
            setUser(undefined)
            setUserProfilePicture(undefined)
            setLoading(false)
        });
    }, [])

    const fetchUserProfile = useCallback(async () => {
        setLoading(true)

        const data = await ProfileApi.getUserProfile()

        if (!data) {
            signOut()
            return
        }

        setUser(data)
        setRoles(data.role || UserRole.None)

        if (data.profilePicture)
            setUserProfilePicture(ImageService.getImageSource(data.profilePicture))

        setLoading(false)
    }, [signOut])


    useEffect(() => {
        if (checkIsLoggedIn()) {
            setIsLoggedIn(true)
            fetchUserProfile()
        }
        setLoading(false);
    }, []);

    const signIn = useCallback(async (username: string, password: string) => {
        const result = await AuthApi.signIn(username, password);
        if (result.isOk) {
            setIsLoggedIn(true)
            fetchUserProfile()
        }
        return result;
    }, [])

    const hasRole = useCallback((role: UserRole) => {
        return (roles & role) === role
    }, [roles])

    const hasAnyRole = useCallback(async (...allowedRoles: UserRole[]) => {
        return allowedRoles.some(role => hasRole(role))
    }, [hasRole, user, fetchUserProfile])

    return (
        <AuthContext.Provider
            value={{
                signIn,
                signOut,
                hasRole,
                hasAnyRole,
                loading,
                isLoggedIn,
                checkIsLoggedIn,
                user,
                userProfilePicture,
                roles
            }}
            {...props}
        />
    );
}

const AuthContext = createContext<AuthContextType>({
    loading: false,
} as AuthContextType);

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
